<form [formGroup]="formGroup" (ngSubmit)="onSubmit(true)" novalidate class="needs-validation">
  @if (isPatientAnonymized()) {
    <div class="alert alert-warning position-sticky">
      {{ 'patient.anonymize.is-anonymized' | translate }}
    </div>
  }

  @if (unsavedChangesExist()) {
    <div class="alert alert-primary border position-sticky">
      <div class="d-flex">
        <div class="flex-grow-1">
          <fa-icon [icon]="'info-circle'" class="me-1 text-primary"></fa-icon>
          {{ 'forms.unsaved-changes' | translate }}
        </div>

        <div>
          <button
            type="button"
            class="btn btn-outline-primary"
            [disabled]="formGroup.invalid || formGroup.pristine"
            (click)="onSubmit(false)"
          >
            {{ 'buttons.save' | translate }}
          </button>
        </div>
      </div>
    </div>
  }

  <fieldset>
    <legend>{{ 'patient.personal' | translate }}</legend>

    <div class="row">
      <div class="form-floating mb-3 col-md-auto">
        <select
          id="title"
          formControlName="title"
          class="form-select title"
          [class.has-value]="formGroup.controls.title.value"
        >
          <option value="" disabled>
            {{ 'patient.title.option.placeholder' | translate }}
          </option>
          @for (title of titleOptions; track title) {
            <option [value]="title">
              {{ 'patient.title.option.' + title | translate }}
            </option>
          }
        </select>
        <label for="title" class="form-label">{{ 'patient.title.label' | translate }}</label>
      </div>

      <div class="form-floating mb-3 col-md-auto">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.initials' | translate }}"
          id="initials"
          formControlName="initials"
          class="form-control initials"
        />
        <label for="initials" class="form-label">{{ 'patient.initials' | translate }}</label>
      </div>

      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.first-name' | translate }}"
          id="first_name"
          formControlName="first_name"
          class="form-control"
        />
        <label for="first_name" class="form-label">{{ 'patient.first-name' | translate }}</label>
      </div>
    </div>

    <div class="row">
      <div class="form-floating mb-3 col-md-auto">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.middle-name' | translate }}"
          id="middle_name"
          formControlName="middle_name"
          class="form-control middle-name"
        />
        <label for="middle_name" class="form-label">{{ 'patient.middle-name' | translate }}</label>
      </div>

      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.last-name' | translate }}"
          id="last_name"
          formControlName="last_name"
          class="form-control"
          [class.is-invalid]="submitted() && formGroup.controls.last_name.errors"
          required
        />
        <label for="last_name" class="form-label">{{ 'patient.last-name' | translate }}*</label>
        <div class="invalid-feedback">{{ 'forms.required' | translate }}</div>
      </div>
    </div>

    <div class="row">
      <div class="form-floating mb-3 col-md">
        <lib-multi-language-datepicker
          formControlName="date_of_birth"
          [required]="true"
          [minDate]="minDateForDateOfBirth"
          [maxDate]="maxDateForDateOfBirth"
          class="form-control p-0 border-0"
          [class.is-invalid]="submitted() && formGroup.controls.date_of_birth.errors"
        ></lib-multi-language-datepicker>
        <label for="date_of_birth" class="form-label">{{ 'patient.date-of-birth' | translate }}*</label>
        <div class="invalid-feedback">
          @if (formGroup.controls.date_of_birth.hasError('required')) {
            <div>{{ 'forms.required' | translate }}</div>
          }
        </div>
        <div class="invalid-feedback">
          @if (formGroup.controls.date_of_birth.hasError('invalidDate')) {
            <div>{{ 'forms.date-not-valid' | translate }}</div>
          }
        </div>
        <div class="invalid-feedback">
          @if (formGroup.controls.date_of_birth.hasError('dateInPast')) {
            <div>{{ 'forms.date-not-in-past' | translate }}</div>
          }
        </div>
      </div>

      <div class="form-floating mb-3 col-md">
        <select
          id="gender"
          formControlName="gender"
          class="form-select"
          [class.has-value]="formGroup.controls.gender.value"
        >
          <option value="" disabled>
            {{ 'patient.gender.option.placeholder' | translate }}
          </option>
          @for (gender of genderOptions; track gender) {
            <option [value]="gender">
              {{ 'patient.gender.option.' + gender.toLowerCase() | translate }}
            </option>
          }
        </select>
        <label for="gender" class="form-label">{{ 'patient.gender.label' | translate }}</label>
      </div>
    </div>
  </fieldset>

  <ng-content select="[after-personal]"></ng-content>

  <fieldset>
    <legend>{{ 'patient.identification' | translate }}</legend>

    <div class="row">
      <div class="form-floating mb-3 col-md">
        <select
          id="id_type"
          formControlName="id_type"
          class="form-select"
          [class.has-value]="formGroup.controls.id_type.value"
        >
          <option value="" disabled>
            {{ 'patient.id-type.option.placeholder' | translate }}
          </option>
          @for (idType of idTypeOptions; track idType) {
            <option [value]="idType">
              {{ 'patient.id-type.option.' + idType.toLowerCase() | translate }}
            </option>
          }
        </select>
        <label for="id_type" class="form-label">{{ 'patient.id-type.label' | translate }}</label>
      </div>

      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.id-number' | translate }}"
          id="id_number"
          formControlName="id_number"
          class="form-control"
        />
        <label for="id_number" class="form-label">{{ 'patient.id-number' | translate }}</label>
      </div>
    </div>

    <div class="row">
      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.bsn-number' | translate }}"
          id="bsn_number"
          formControlName="bsn_number"
          class="form-control"
          [class.is-invalid]="submitted() && formGroup.controls.bsn_number.errors"
        />
        <label for="bsn_number" class="form-label">{{ 'patient.bsn-number' | translate }}</label>
        <div class="invalid-feedback">
          @if (formGroup.controls.bsn_number.hasError('bsn')) {
            {{ 'forms.invalid-bsn' | translate }}
          }
        </div>
      </div>

      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.external-id' | translate }}"
          id="external_id"
          formControlName="external_id"
          class="form-control"
        />
        <label for="external_id" class="form-label">{{ 'patient.external-id' | translate }}</label>
      </div>
    </div>
  </fieldset>

  <fieldset>
    <legend>{{ 'patient.insurance' | translate }}</legend>

    <div class="row">
      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.health-insurer' | translate }}"
          id="health_insurer"
          formControlName="health_insurer"
          class="form-control"
        />
        <label for="health_insurer" class="form-label">{{ 'patient.health-insurer' | translate }}</label>
      </div>

      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.health-insurance-number' | translate }}"
          id="health_insurance_number"
          formControlName="health_insurance_number"
          class="form-control"
        />
        <label for="health_insurance_number" class="form-label">
          {{ 'patient.health-insurance-number' | translate }}
        </label>
      </div>
    </div>
  </fieldset>

  <fieldset>
    <legend>{{ 'common.address' | translate }}</legend>

    <div class="row">
      <div class="form-floating mb-3 col-md-6">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'common.street' | translate }}"
          id="street"
          formControlName="street"
          class="form-control"
        />
        <label for="street" class="form-label">{{ 'common.street' | translate }}</label>
      </div>

      <div class="form-floating mb-3 col-md-3">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'common.house-number.house-number' | translate }}"
          id="house_number"
          formControlName="house_number"
          class="form-control"
        />
        <label for="house_number" class="form-label">{{ 'common.house-number' | translate }}</label>
      </div>

      <div class="form-floating mb-3 col-md-3">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'common.postal-code' | translate }}"
          id="postal_code"
          formControlName="postal_code"
          class="form-control"
        />
        <label for="postal_code" class="form-label">{{ 'common.postal-code' | translate }}</label>
      </div>
    </div>

    <div class="row">
      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'common.city' | translate }}"
          id="city"
          formControlName="city"
          class="form-control"
        />
        <label for="city" class="form-label">{{ 'common.city' | translate }}</label>
      </div>

      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.province' | translate }}"
          id="province"
          formControlName="province"
          class="form-control"
        />
        <label for="province" class="form-label">{{ 'patient.province' | translate }}</label>
      </div>
    </div>

    <div class="row">
      <div class="form-floating mb-3">
        <lib-country-selector
          formControlName="country"
          (countriesFetched)="setPatientsCountry()"
          class="form-control p-0 border-0"
        ></lib-country-selector>
        <label for="country" class="form-label">{{ 'common.country' | translate }}</label>
      </div>
    </div>

    <div class="row">
      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'common.phone' | translate }}"
          id="phone"
          formControlName="phone"
          class="form-control"
        />
        <label for="phone" class="form-label">{{ 'common.phone' | translate }}</label>
      </div>

      <div class="form-floating mb-3 col-md">
        <input
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
          autocomplete="off"
          placeholder="{{ 'patient.mobile' | translate }}"
          id="mobile"
          formControlName="mobile"
          class="form-control"
        />
        <label for="mobile" class="form-label">{{ 'patient.mobile' | translate }}</label>
      </div>
    </div>

    <div class="form-floating mb-3">
      <input
        data-1p-ignore
        data-bwignore
        data-lpignore="true"
        data-form-type="other"
        autocomplete="off"
        type="email"
        placeholder="{{ 'common.email' | translate }}"
        id="email"
        formControlName="email"
        class="form-control"
      />
      <label for="email" class="form-label">{{ 'common.email' | translate }}</label>
      <div class="invalid-feedback">
        @if (formGroup.controls.email.hasError('email')) {
          {{ 'forms.invalid-email' | translate }}
        }
      </div>
    </div>
  </fieldset>

  <fieldset>
    <legend>{{ 'patient.status.label' | translate }}</legend>

    <div class="row">
      <div class="form-floating mb-3 col-md-auto">
        <select
          id="status"
          formControlName="status"
          class="form-select"
          [class.has-value]="formGroup.controls.status.value"
        >
          @if (!isPatientAnonymized()) {
            <option value="" disabled>
              {{ 'patient.status.option.placeholder' | translate }}
            </option>
          }
          @for (status of statusOptions(); track status) {
            <option [value]="status">
              {{ 'patient.status.option.' + status.toLowerCase() | translate }}
            </option>
          }
        </select>
        <label for="status" class="form-label">{{ 'patient.status.label' | translate }}</label>
      </div>
    </div>

    <div class="form-floating mb-3">
      <input
        data-1p-ignore
        data-bwignore
        data-lpignore="true"
        data-form-type="other"
        autocomplete="off"
        placeholder="{{ 'patient.referrer' | translate }}"
        id="referrer"
        formControlName="referrer"
        class="form-control"
      />
      <label for="referrer" class="form-label">{{ 'patient.referrer' | translate }}</label>
    </div>

    <div class="mb-3">
      <label for="notes" class="form-label">{{ 'patient.notes' | translate }}</label>
      <textarea id="notes" formControlName="notes" class="form-control" rows="5"></textarea>
    </div>
  </fieldset>

  @if (errorMessage()) {
    <div class="alert alert-danger mb-3">{{ errorMessage() }}</div>
  }

  <div>
    <button type="button" class="btn btn-outline-primary me-2" [disabled]="isLoading()" (click)="onSubmit(false)">
      {{ 'patient.submit' | translate }}
    </button>

    <button type="submit" class="btn btn-primary">
      {{ 'patient.submit-and-start-consult' | translate }}
    </button>
  </div>
</form>
